import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, Loader2, Hash, Plus, AlertCircle, Sparkles, X, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const API_URL = window.__WR_API_BASE__;

/**
 * SmartICD10Search - LLM-powered ICD-10 code search
 * Allows users to search for ICD-10 codes using natural language descriptions.
 */
export const SmartICD10Search = ({ 
  onSelectCode,  // Callback when user selects a code
  className = ""
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setResults([]);
      setError(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      console.log('[SmartICD10] Searching for:', searchQuery);
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${API_URL}/api/icd10/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery, max_results: 8 }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      console.log('[SmartICD10] Response status:', response.status);
      
      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      console.log('[SmartICD10] Results:', data.results?.length || 0, data);
      setResults(data.results || []);
      
      if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      console.error('[SmartICD10] search error:', err);
      if (err.name === 'AbortError') {
        setError('Search timed out. Please try again.');
      } else {
        setError('Search failed. Please try again.');
      }
      setResults([]);
    } finally {
      setIsSearching(false);
      console.log('[SmartICD10] Search complete');
    }
  }, []);

  // Handle input change with debounce
  const handleQueryChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(newQuery);
    }, 500); // 500ms debounce
  };

  // Handle code selection
  const handleSelectCode = (result) => {
    const isSelected = selectedCodes.some(c => c.code === result.code);
    
    if (isSelected) {
      setSelectedCodes(prev => prev.filter(c => c.code !== result.code));
    } else {
      setSelectedCodes(prev => [...prev, result]);
    }

    // Call parent callback if provided
    if (onSelectCode) {
      onSelectCode(result, !isSelected);
    }
  };

  // Copy code to clipboard
  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Clear search
  const handleClear = () => {
    setQuery('');
    setResults([]);
    setError(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Get category color based on ICD-10 chapter
  const getCategoryColor = (category) => {
    const colors = {
      'Musculoskeletal': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      'Mental/Behavioral': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      'Nervous system': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
      'Injury/Poisoning': 'bg-red-500/20 text-red-300 border-red-500/30',
      'Circulatory': 'bg-rose-500/20 text-rose-300 border-rose-500/30',
      'Respiratory': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      'Symptoms/Signs': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      'Endocrine/Metabolic': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      'Health status/Services': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      'External causes': 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    };
    return colors[category] || 'bg-secondary text-muted-foreground border-border';
  };

  return (
    <div className={`space-y-3 ${className}`} data-testid="smart-icd10-search">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-medium text-foreground">Smart ICD-10 Search</span>
        <Badge variant="outline" className="text-[10px] h-4 px-1.5 bg-amber-500/10 border-amber-500/30 text-amber-400">
          AI-Powered
        </Badge>
      </div>

      {/* Search Input */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
          {isSearching ? (
            <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
        <Input
          ref={inputRef}
          type="text"
          placeholder="Describe condition... e.g., 'chronic knee pain after ACL surgery'"
          value={query}
          onChange={handleQueryChange}
          className="pl-10 pr-10 h-10 text-sm bg-input border-border"
          data-testid="icd10-search-input"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-secondary"
            data-testid="icd10-search-clear"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Search hint */}
      {!query && results.length === 0 && (
        <p className="text-xs text-muted-foreground italic">
          Type a symptom, condition, or diagnosis in plain language
        </p>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-2" data-testid="icd10-search-results">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {results.length} code{results.length !== 1 ? 's' : ''} found
            </span>
            {selectedCodes.length > 0 && (
              <span className="text-xs text-emerald-400">
                {selectedCodes.length} selected
              </span>
            )}
          </div>
          
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
            {results.map((result, idx) => {
              const isSelected = selectedCodes.some(c => c.code === result.code);
              const isCopied = copiedCode === result.code;
              
              return (
                <div
                  key={idx}
                  className={`p-2.5 rounded-lg border transition-all ${
                    isSelected
                      ? 'bg-emerald-500/10 border-emerald-500/40 ring-1 ring-emerald-500/20'
                      : 'bg-secondary/50 border-border hover:border-primary/40'
                  }`}
                  data-testid={`icd10-result-${result.code}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {/* Code and Category */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className={`font-mono font-bold text-sm ${isSelected ? 'text-emerald-300' : 'text-foreground'}`}>
                          {result.code}
                        </code>
                        <Badge 
                          variant="outline" 
                          className={`text-[9px] h-4 px-1.5 border ${getCategoryColor(result.category)}`}
                        >
                          {result.category}
                        </Badge>
                      </div>
                      
                      {/* Description */}
                      <p className={`text-sm mt-1 ${isSelected ? 'text-emerald-100' : 'text-foreground'}`}>
                        {result.description}
                      </p>
                      
                      {/* Relevance note */}
                      {result.relevance_note && (
                        <p className="text-xs text-muted-foreground mt-1 italic">
                          {result.relevance_note}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => handleCopyCode(result.code)}
                        title="Copy code"
                        data-testid={`copy-${result.code}`}
                      >
                        {isCopied ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                        )}
                      </Button>
                      <Button
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        className={`h-7 px-2 text-xs ${isSelected ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                        onClick={() => handleSelectCode(result)}
                        data-testid={`select-${result.code}`}
                      >
                        {isSelected ? (
                          <>
                            <Check className="w-3 h-3 mr-1" />
                            Added
                          </>
                        ) : (
                          <>
                            <Plus className="w-3 h-3 mr-1" />
                            Add
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Disclaimer */}
          <p className="text-[10px] text-muted-foreground italic mt-2">
            AI suggestions may be inaccurate. Always verify codes against official ICD-10-CM guidelines.
          </p>
        </div>
      )}

      {/* Loading state */}
      {isSearching && results.length === 0 && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <span className="ml-2 text-sm text-muted-foreground">Searching ICD-10 codes...</span>
        </div>
      )}
    </div>
  );
};

export default SmartICD10Search;
